import React, { useState, useEffect } from 'react';
import { ChevronRight, CheckCircle } from 'lucide-react';
import { useAsistencia } from '@/hooks/useAsistencia';
import { useJugadoresEquipoActivo } from '@/hooks/useJugadoresEquipoActivo';
import { ModalAsistencia } from '../components/ModalAsistencia';
import { Card } from '../components/ui/Card';
import { DiaEntrenamiento } from '@/types/asistencia';

/**
 * Página de gestión de asistencia a entrenamientos
 */
const Asistencia: React.FC = () => {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [diaSeleccionado, setDiaSeleccionado] = useState<DiaEntrenamiento | null>(null);
  const [diasEntrenamiento, setDiasEntrenamiento] = useState<DiaEntrenamiento[]>([]);
  
  const { obtenerDiasEntrenamiento } = useAsistencia();
  const { jugadores, loading: loadingJugadores } = useJugadoresEquipoActivo();

  useEffect(() => {
    const dias = obtenerDiasEntrenamiento();
    setDiasEntrenamiento(dias);
  }, []);

  /**
   * Maneja el click en una tarjeta de día
   */
  const handleClickDia = (dia: DiaEntrenamiento) => {
    setDiaSeleccionado(dia);
    setModalAbierto(true);
  };

  /**
   * Maneja el cierre del modal
   */
  const handleCerrarModal = () => {
    setModalAbierto(false);
    setDiaSeleccionado(null);
  };

  if (loadingJugadores) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4">
      <div className="space-y-6">
        {/* Descripción de la página */}
        <div className="bg-secondary-800 rounded-xl p-6 border border-secondary-700">
          <p className="text-secondary-400">
            Registra la asistencia de los jugadores a los entrenamientos de la semana actual.
          </p>
        </div>
        
        {/* Tarjetas de días de entrenamiento */}
        <div className="space-y-4">
          {diasEntrenamiento.map((dia) => (
            <Card
              key={dia.fecha}
              variant="elevated"
              onClick={() => handleClickDia(dia)}
              className="p-6 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-secondary-50 mb-1">
                    {dia.dia}
                  </h3>
                  <p className="text-secondary-400 text-sm">
                    {dia.numero}
                  </p>
                  {dia.tieneRegistros && (
                    <div className="flex items-center mt-3 space-x-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span className="text-emerald-500 text-xs font-medium">
                        Registrado
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-primary-500">
                  <ChevronRight className="w-6 h-6" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Modal de asistencia */}
      {modalAbierto && diaSeleccionado && (
        <ModalAsistencia
          dia={diaSeleccionado}
          jugadores={jugadores}
          onCerrar={handleCerrarModal}
          onGuardar={() => {
            // Actualizar estado si es necesario
            handleCerrarModal();
          }}
        />
      )}
    </div>
  );
};

export default Asistencia;